<template>
  <v-app class="bgImage">
    <m-errorbox ref="errorBox"></m-errorbox>

    <navbar>
      <v-toolbar-title
        v-if="!loaded"
        class="text-uppercase secondary--text text--darken-3"
      >
        <span class="font-weight-light">Please </span>
        <span class="font-weight-medium">Wait...</span>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <m-menu v-if="loaded" icon="mdi-table-settings" label="Table View">
        <m-item icon="mdi-table-column-remove" @click="onMenuTableHide"
          >Hide Table</m-item
        >
        <m-item icon="mdi-table-large" @click="onMenuTableAll"
          >All Readings</m-item
        >
        <m-item icon="mdi-chart-timeline-variant" @click="onMenuTableMaxMin"
          >Daily Max/Min</m-item
        >
      </m-menu>

      <v-menu offset-y v-if="loaded" style="z-index:10;">
        <template v-slot:activator="{ on }">
          <v-btn
            text
            class="secondary--text text--darken-3 ml-3"
            v-on="on"
            dense
          >
            <v-icon left>mdi-calendar</v-icon>
            Pick Date
          </v-btn>
        </template>
        <v-list dense class="pt-0 pb-0">
          <v-date-picker
            :value="dateString"
            @input="onDateSelect"
            :min="
              $store.state.device &&
                new Date(
                  $store.state.device.first_reading * 1000 + 24 * 60 * 60
                )
                  .toISOString()
                  .split('T')[0]
            "
            :max="
              $store.state.device &&
                new Date($store.state.device.last_reading * 1000 - 24 * 60 * 60)
                  .toISOString()
                  .split('T')[0]
            "
          ></v-date-picker>
        </v-list>
      </v-menu>

      <v-spacer></v-spacer>
    </navbar>

    <v-content>
      <v-container
        v-show="!(loaded || linkExpired || linkInvalid)"
        fluid
        fill-height
      >
        <v-row justify="center" align="center">
          <v-col justify="center" align="center">
            <v-progress-circular
              :value="apiRequestProgress.toFixed()"
              :indeterminate="apiRequestProgress == 100"
              size="250"
            ></v-progress-circular>
            <h1 v-if="apiRequestProgress == 100" class="mt-4">Loading...</h1>
            <h1 v-else class="mt-4">
              Downloading {{ apiRequestProgress.toFixed() }}% ...
            </h1>
          </v-col>
        </v-row>
      </v-container>

      <v-container v-show="loaded" fluid fill-height>
        <v-row :style="'height: 70%'">
          <v-col>
            <v-card tile class="mb-2 pr-5 pb-2 pt-2 pl-2" height="100%">
              <m-graph
                :model="$store.state.log"
                :modelVersion="$store.state.logVersion"
                @highlightrow="highLightRow"
                @loaded="loaded = true"
                :controlIndex="0"
                :highlight-pos.sync="highlightedIndex"
                :date-window.sync="dateWindow"
                :legendVisible="tableView == 'hide'"
              ></m-graph>
            </v-card>
          </v-col>

          <!-- THE TABLE VIEW -->
          <v-col
            v-if="tableView != 'hide'"
            cols="auto"
            class="fill-height"
            style="padding:12;"
          >
            <m-table
              v-if="tableView == 'all'"
              @resize="triggerResize"
              :numGraphs="numGraphs"
            ></m-table>
            <m-maxmin
              v-else-if="tableView == 'maxmin'"
              @resize="triggerResize"
              :numGraphs="numGraphs"
            ></m-maxmin>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-card tile class="mb-5 pr-5 pb-2 pt-2 pl-2">
              <v-card-text>
                <v-data-table
                  :headers="dataTableHeaders"
                  :items="dataTableData"
                  page-sync="1"
                  :items-per-page.sync="dataTableData.length"
                  hide-default-footer
                >
                  <template v-slot:item="{ item, index }">
                    <tr
                      class="text-no-wrap"
                      style="cursor: pointer;"
                      @click="onRowClicked(item.device_token)"
                    >
                      <td
                        v-for="header in dataTableHeaders"
                        :key="header.value + index.toString()"
                        :style="`color: ${item.color}; fontWeight: bold`"
                      >
                        {{ item[header.value] }}
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-content>
  </v-app>
</template>

<script>
import usbRequest from "../mixins/usb-request";
import apiEndpoints from "../mixins/api-endpoints";
import apiRequest from "../mixins/api-request";
import formatDate from "../mixins/format-date";
import store from "../store";

export default {
  mixins: [usbRequest, apiEndpoints, apiRequest, formatDate],
  data() {
    return {
      loaded: false,
      highlightedIndex: null,
      dateWindow: [],
      allDevicesDetails: [],
      linkExpired: false,
      linkInvalid: false,
      tableView: "hide",
      dateString: "",
      numGraphs: 1,
      dataTableHeaders: [
        { text: "Partial token", value: "partial_token" },
        { text: "Site Name", value: "device_name" },
        { text: "Address", value: "address" },
        { text: "City", value: "city" },
        { text: "State", value: "state" },
        { text: "Postcode", value: "postcode" }
      ]
    };
  },
  computed: {
    dataTableData() {
      return this.$store.state.devices.map((item, idx) => {
        const device = Object.assign({}, item);

        // get the trace color from the graph
        // there is only one chart group so grab the first one
        if (
          this.$store.state.chartGroup[0] &&
          this.$store.state.chartGroup[0].series[device.partial_token]
        ) {
          device.color = this.$store.state.chartGroup[0].series[
            device.partial_token
          ].color;
        } else {
          device.color = "red";
        }

        return device;
      });
    }
  },
  methods: {
    onRowClicked(device_token) {
      const devices = this.$store.state.devices;
      const foundDevice = devices.find(device =>
        device.device_token.endsWith(device_token)
      );
      if (foundDevice) {
        // Go to the found device
        this.$router.push("/deviceinfo/" + foundDevice.id);
      }
    },
    showError(message) {
      this.$refs.errorBox.showError(message);
    },
    triggerResize() {
      // trigger a resize event so that the components redraw
      // I'm just using setTimeout to add the event after the call stack.
      window.setTimeout(() => {
        // chrome/ff
        //window.dispatchEvent(new Event('resize'));

        // IE/chrome/ff
        var resizeEvent = window.document.createEvent("UIEvents");
        resizeEvent.initUIEvent("resize", true, false, window, 0);
        window.dispatchEvent(resizeEvent);
      }, 100);
    },
    onMenuTableHide(evt) {
      console.log("MenuTableHide");
      this.tableView = "hide";
      this.triggerResize();
    },
    onMenuTableAll(evt) {
      console.log("MenuTableAll");
      this.tableView = "all";
      this.triggerResize();
    },
    onMenuTableMaxMin(evt) {
      console.log("MenuTableMaxMin");
      this.tableView = "maxmin";
      this.triggerResize();
    },
    onDateSelect(dateString) {
      var timeStamp = new Date(dateString).getTime();
      this.loadChartDataFromServer(timeStamp);
    },
    loadChartDataFromServer(atDate) {
      const selectedGraphLine = this.$route.query.selected_graph_line || "cli";
      const devices = this.$route.query["devices[]"];
      const params = new URLSearchParams();
      params.append("selected_graph_line", selectedGraphLine);
      devices.forEach(device => params.append("devices[]", device));

      if (devices.length > 0) {
        if (atDate) {
          params.append("starting_at", atDate / 1000 - 14 * 24 * 60 * 60);
          params.append("ending_at", atDate / 1000 + 14 * 24 * 60 * 60);
        }

        this.loaded = false;
        this.apiRequest(
          this.apiEndpoints.multipleDeviceLogs,
          null,
          params.toString(),
          {
            Accept: "application/octet-stream"
          }
        )
          .then(r => {
            if (r == null) {
              console.error("ApiRequest returned an empty response.");
              this.showError(
                "The server returned an empty response.  Try going back to the home page!"
              );
            } else if (this.checkStatus(r, "Error getting file from server.")) {
              this.loadChartResponse(r.body);
            }
            this.loaded = true;
          })
          .catch(ex => {
            console.log(ex, "Error loading chart data from server.");
            // if there is an error loading the log (perhaps a bad bookmark, or not logged in) just redirect
            // to the home page
            this.$router.replace("/");
          });
      }
    },
    highLightRow(evt) {
      this.$store.commit("setSelectedTimestamp", evt);
    }
  },
  mounted() {
    this.loadChartDataFromServer();
  }
};
</script>
